import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  Check,
  Download,
  Eye,
  Loader2,
  Mail,
  MoreVertical,
  Plus,
  Search,
  Send,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { usePermissions } from '@/hooks/usePermissions'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { ListExportDialog, LoadingOverlay } from '@/components/shared'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import {
  InvoiceStatusBadge,
  ClientNameWithProspect,
} from '@/components/shared/StatusBadges'
import { BulkSendInvoicesDialog } from '@/components/billing/BulkSendInvoicesDialog'
import { ClientMultiSelectFilter } from '@/components/billing/ClientMultiSelectFilter'
import { InvoiceSendDialog } from '@/components/billing/InvoiceSendDialog'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { JobDetailDrawer } from '@/components/jobs/JobDetailDrawer'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { useInvoicesList, useInvoicesSummary } from '@/hooks/useInvoices'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { INVOICE_MODE } from '@/lib/constants'
import { formatCurrency, formatDate, formatInteger } from '@/lib/format'
import { primaryEmail } from '@/lib/clientContacts'
import { invoicesApi } from '@/services/invoices'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import {
  UNPAID_INVOICE_STATUSES,
  type Invoice,
  type InvoiceListQuery,
} from '@/types'
import totalInvoicesIcon from '@/assets/invoices/metric-total.svg'
import paidInvoicesIcon from '@/assets/invoices/metric-paid.svg'
import pendingInvoicesIcon from '@/assets/invoices/metric-pending.svg'
import overdueInvoicesIcon from '@/assets/invoices/metric-overdue.svg'

const STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'pro_forma', labelKey: 'list.status.proForma' },
  { value: 'pro_forma_sent', labelKey: 'list.status.proFormaSent' },
  { value: 'validated', labelKey: 'list.status.validated' },
  { value: 'pending', labelKey: 'list.status.pending' },
  { value: 'paid', labelKey: 'list.status.paid' },
  { value: 'partially_paid', labelKey: 'list.status.partiallyPaid' },
  { value: 'overdue', labelKey: 'list.status.overdue' },
  {
    value: 'cancelled_via_credit_note',
    labelKey: 'list.status.cancelledViaCreditNote',
  },
]

// Toolbar filter trigger styling (Figma 1196:32987): 40px tall, rounded-8,
// #d9d9d9 border, 14px regular text — shared with the Quotes list toolbar.
const filterTriggerClass =
  'h-10 w-auto shrink-0 justify-between gap-2 rounded-xl border-[#e7e9ef] bg-white py-3 pl-4 pr-3 text-sm font-normal leading-[1.4] text-[#171a20] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&_svg]:size-4'

const toolbarButtonClass =
  'h-10 shrink-0 rounded-xl border-[#e7e9ef] bg-white px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] hover:bg-accent [&_svg]:size-4'
const bulkPrimaryButtonClass =
  'h-9 shrink-0 gap-2 rounded-lg border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-2 text-sm font-normal leading-[1.4] text-white hover:bg-[#e6511c]'
const bulkSecondaryButtonClass =
  'h-9 shrink-0 gap-2 rounded-lg border border-[#e7e9ef] bg-white px-4 py-2 text-sm font-normal leading-[1.4] text-[#171a20] hover:bg-[#fbfbfc]'
const tableCheckboxClass =
  'size-4 rounded border-[#b0b5c3] bg-white text-white shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=checked]:border-[#ff5a1f] data-[state=checked]:bg-[#ff5a1f] data-[state=indeterminate]:border-[#ff5a1f] data-[state=indeterminate]:bg-[#ff5a1f]'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'

const tdClass =
  'h-16 overflow-hidden border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm text-[#171a20]'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] hover:bg-[#fbfbfc]'
const accentActionBtnClass = cn(
  actionBtnClass,
  'text-sm font-normal border border-transparent hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
)
const actionMenuTriggerClass = cn(
  accentActionBtnClass,
  'data-[state=open]:border-[#ff5a1f] data-[state=open]:bg-[#fff1ea] data-[state=open]:text-[#ff5a1f] data-[state=open]:group-hover:bg-[#fff1ea] data-[state=open]:hover:bg-[#fff1ea]'
)
const finalizeBtnClass =
  'inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-lg border border-[#059669] bg-[#ecfdf5] px-2 text-xs font-medium leading-none text-[#047857] hover:bg-[#dcfce7]'
const rowMenuContentClass =
  'w-[200px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_8px_-1px_rgba(38,45,66,0.1),0px_1px_4px_-1px_rgba(38,45,66,0.05)]'
const rowMenuItemClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20] data-[disabled]:opacity-50'
const invoiceActionsGroupClass = 'ml-auto flex items-center justify-end gap-2'
const invoiceActionsHeaderClass = 'ml-auto flex items-center justify-end gap-2'

const actionIconClass = 'size-4 shrink-0'

// Figma 1085:12466 — TopNavi primary CTA (New Invoice).
const topBarNewInvoiceClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

const summaryCardInteractionClass =
  'cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
const summaryCardActiveColor = '#ff5a1f'

/** Figma 1085:12491 column widths (9 columns, min table width 1152px). */
const COL = {
  checkbox: 'w-[40px] max-w-[40px] text-center pl-0',
  clientName: 'w-[200px] min-w-[200px] text-left',
  title: 'w-[160px] min-w-[160px] max-w-[160px] text-left',
  amount: 'w-[120px] min-w-[120px] text-center',
  status: 'w-[144px] min-w-[144px] text-center',
  dueDate: 'w-[144px] min-w-[144px] text-left',
  number: 'w-[120px] min-w-[120px] text-left',
  linkedJob: 'w-[176px] min-w-[176px] text-left',
  actions: 'w-[142px] min-w-[142px] max-w-[142px] text-right',
} as const

const TABLE_MIN_WIDTH = 1224
const COLUMN_COUNT = 9

// Metric card themes (Figma 2989:34295).
type InvoiceCardKey = 'total' | 'paid' | 'pending' | 'overdue'

function fromCsv(value: string | undefined): string[] {
  return value ? value.split(',').filter(Boolean) : []
}

function toCsv(values: string[]): string | undefined {
  return values.length ? values.join(',') : undefined
}

function sameValues(left: string[], right: string[]): boolean {
  return (
    left.length === right.length && left.every(value => right.includes(value))
  )
}

const CARD_THEMES: Record<
  InvoiceCardKey,
  {
    bg: string
    border: string
    iconSrc: string
    valueColor?: string
  }
> = {
  total: {
    bg: '#ffffff',
    border: '#e7e9ef',
    iconSrc: totalInvoicesIcon,
  },
  paid: {
    bg: '#ecfdf5',
    border: '#a7f3d0',
    iconSrc: paidInvoicesIcon,
  },
  pending: {
    bg: '#fbf3d9',
    border: '#fde68a',
    iconSrc: pendingInvoicesIcon,
  },
  overdue: {
    bg: '#fde9e8',
    border: '#fca5a5',
    iconSrc: overdueInvoicesIcon,
    valueColor: '#d63b33',
  },
}

const CARD_STATUS_FILTERS: Record<InvoiceCardKey, string[]> = {
  total: [],
  paid: ['paid'],
  pending: ['pending', 'partially_paid'],
  overdue: ['overdue'],
}

// 2.6.1 Invoice list - summary cards, unpaid-by-client section, bulk actions,
// list export, search/filter, pagination, row Edit/Delete.
export default function InvoicesListPage() {
  const { t } = useTranslation('invoices')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { canEdit } = usePermissions()
  const canEditInvoices = canEdit('invoices')

  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<{
    status?: string
    client_id?: string
    date_from?: string
    date_to?: string
  }>({})

  // The multi-client selection is stored in the URL as a comma-joined `client_id`
  // param (keeps the list shareable). It is expanded back to an array both for
  // the filter UI and for the API call (sent as `client_id[]=...`).
  const clientIds = useMemo(
    () =>
      filters.client_id ? filters.client_id.split(',').filter(Boolean) : [],
    [filters.client_id]
  )
  const selectedStatuses = useMemo(
    () => fromCsv(filters.status),
    [filters.status]
  )
  const apiQuery = useMemo<InvoiceListQuery>(() => {
    const {
      job: _job,
      job_tab: _jobTab,
      status: _status,
      ...listQuery
    } = query as InvoiceListQuery & { job?: string; job_tab?: string }
    void _job
    void _jobTab
    void _status

    return {
      ...listQuery,
      status: selectedStatuses.length ? selectedStatuses : undefined,
      client_id: clientIds.length ? clientIds : undefined,
    }
  }, [query, selectedStatuses, clientIds])

  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useInvoicesList(apiQuery)
  const { data: summaryData } = useInvoicesSummary()
  const summary = summaryData?.data ?? null
  const invoices = useMemo(() => data?.data ?? [], [data])

  const [selectedInvoicesById, setSelectedInvoicesById] = useState<
    Map<string, Invoice>
  >(new Map())
  const selected = useMemo(
    () => new Set(selectedInvoicesById.keys()),
    [selectedInvoicesById]
  )
  const [bulkOpen, setBulkOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [bulkAction, setBulkAction] = useState<
    'chase' | 'export' | 'markPaid' | null
  >(null)
  const busy = bulkAction !== null
  // Row-level "Send to client" — opens the shared send dialog inline instead of
  // navigating to the invoice detail page.
  const [sendTarget, setSendTarget] = useState<Invoice | null>(null)
  const [exportingPdfId, setExportingPdfId] = useState<string | null>(null)
  const selectedJobId = searchParams.get('job')

  const openJobDrawer = (jobId: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('job', jobId)
        next.delete('job_tab')
        return next
      },
      { replace: false }
    )
  }

  const closeJobDrawer = () => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('job')
        next.delete('job_tab')
        return next
      },
      { replace: false }
    )
  }

  const newInvoiceAction = useMemo(
    () => (
      <Permit module='invoices' action='edit'>
        <Button
          className={topBarNewInvoiceClass}
          onClick={() => navigate('/invoices/new')}
        >
          <Plus className='size-4' />
          {t('list.createInvoice')}
        </Button>
      </Permit>
    ),
    [navigate, t]
  )
  useTopBarActions(newInvoiceAction)

  const totalRows = data?.metadata?.total ?? invoices.length
  const selectedInvoices = useMemo(
    () => Array.from(selectedInvoicesById.values()),
    [selectedInvoicesById]
  )
  const currentPageSelectedCount = useMemo(
    () =>
      invoices.reduce(
        (count, invoice) => count + (selected.has(invoice.id) ? 1 : 0),
        0
      ),
    [invoices, selected]
  )
  const selectedUnpaidInvoices = useMemo(
    () =>
      selectedInvoices.filter(i => UNPAID_INVOICE_STATUSES.includes(i.status)),
    [selectedInvoices]
  )
  const canSendUnpaidReminders =
    selected.size > 0 &&
    selectedInvoices.length === selected.size &&
    selectedUnpaidInvoices.length === selected.size
  const selectedUnpaidTotal = selectedUnpaidInvoices.reduce(
    (sum, inv) => sum + (inv.total || 0),
    0
  )
  const totalInvoiceCount = summary
    ? summary.paid.count + summary.pending.count + summary.overdue.count
    : null

  const toggle = (invoice: Invoice) => {
    setSelectedInvoicesById(prev => {
      const next = new Map(prev)
      if (next.has(invoice.id)) next.delete(invoice.id)
      else next.set(invoice.id, invoice)
      return next
    })
  }
  const toggleAll = () => {
    setSelectedInvoicesById(prev => {
      const next = new Map(prev)
      const allVisibleSelected =
        invoices.length > 0 && invoices.every(invoice => next.has(invoice.id))

      invoices.forEach(invoice => {
        if (allVisibleSelected) next.delete(invoice.id)
        else next.set(invoice.id, invoice)
      })

      return next
    })
  }
  const clearSelection = () => setSelectedInvoicesById(new Map())

  const selectedIds = Array.from(selected)

  const bulkChase = async () => {
    setBulkAction('chase')
    try {
      await invoicesApi.bulkChase({ invoice_ids: selectedIds })
      toast({ variant: 'success', title: t('list.bulkChaseSuccess') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('list.bulkChaseError')),
      })
    } finally {
      setBulkAction(null)
    }
  }

  const bulkExport = async () => {
    setBulkAction('export')
    try {
      const blob = await invoicesApi.bulkExportZip({ invoice_ids: selectedIds })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Invoices_${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('list.bulkExportError')),
      })
    } finally {
      setBulkAction(null)
    }
  }

  const exportPdf = async (inv: Invoice) => {
    setExportingPdfId(inv.id)
    try {
      const res = await invoicesApi.pdf(inv.id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('list.bulkExportError')),
      })
    } finally {
      setExportingPdfId(null)
    }
  }

  const bulkMarkPaid = async () => {
    setBulkAction('markPaid')
    try {
      await invoicesApi.bulkMarkPaid({ invoice_ids: selectedIds })
      toast({ variant: 'success', title: t('list.bulkMarkPaidSuccess') })
      setMarkPaidOpen(false)
      // Refetch so the rows flip to "Paid" and the summary cards update
      // immediately, instead of only after a manual refresh (bug 2.6.1).
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['invoices', 'summary'] })
      invalidatePortalMetricQueries(queryClient)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('list.bulkMarkPaidError')),
      })
    } finally {
      setBulkAction(null)
    }
  }

  const goToPaymentTab = (inv: Invoice) =>
    navigate(`/invoices/${inv.id}?tab=payments`)

  const viewAction = (inv: Invoice) => (
    <TruncatedTextTooltip
      content={t('list.viewInvoice')}
      delayDuration={0}
      alwaysShow
      closeOnPointerDown
      className='inline-flex shrink-0'
    >
      <button
        type='button'
        className={accentActionBtnClass}
        aria-label={t('list.viewInvoice')}
        onClick={() => navigate(`/invoices/${inv.id}`)}
      >
        <Eye className={actionIconClass} strokeWidth={2} />
      </button>
    </TruncatedTextTooltip>
  )

  const sendAction = (inv: Invoice) => (
    <Permit module='invoices' action='edit'>
      <TruncatedTextTooltip
        content={t('list.send')}
        delayDuration={0}
        alwaysShow
        closeOnPointerDown
        className='inline-flex shrink-0'
      >
        <button
          type='button'
          className={accentActionBtnClass}
          aria-label={t('list.send')}
          onClick={() => setSendTarget(inv)}
        >
          <Send className={actionIconClass} strokeWidth={2} />
        </button>
      </TruncatedTextTooltip>
    </Permit>
  )

  const downloadAction = (inv: Invoice) => (
    <TruncatedTextTooltip
      content={t('list.download')}
      delayDuration={0}
      alwaysShow
      closeOnPointerDown
      className='inline-flex shrink-0'
    >
      <button
        type='button'
        className={accentActionBtnClass}
        aria-label={t('list.download')}
        disabled={exportingPdfId === inv.id}
        onClick={() => exportPdf(inv)}
      >
        <Download className={actionIconClass} strokeWidth={2} />
      </button>
    </TruncatedTextTooltip>
  )

  const tickAction = (inv: Invoice) => (
    <Permit module='payments' action='edit'>
      <TruncatedTextTooltip
        content={t('list.recordPayment')}
        delayDuration={0}
        alwaysShow
        closeOnPointerDown
        className='inline-flex shrink-0'
      >
        <button
          type='button'
          className={accentActionBtnClass}
          aria-label={t('list.recordPayment')}
          onClick={() => goToPaymentTab(inv)}
        >
          <Check className={actionIconClass} strokeWidth={2.4} />
        </button>
      </TruncatedTextTooltip>
    </Permit>
  )

  const finalizeAction = (inv: Invoice) => (
    <Permit module='invoices' action='edit'>
      <button
        type='button'
        className={finalizeBtnClass}
        aria-label={t('list.finalise')}
        onClick={() => navigate(`/invoices/${inv.id}/edit?finalise=1`)}
      >
        {t('list.finalise')}
        <ArrowRight className='size-4 shrink-0' strokeWidth={2} />
      </button>
    </Permit>
  )

  const moreMenuAction = (inv: Invoice) => (
    <DropdownMenu>
      <TruncatedTextTooltip
        content={t('list.moreActions')}
        delayDuration={0}
        alwaysShow
        closeOnPointerDown
        className='inline-flex shrink-0'
      >
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            className={actionMenuTriggerClass}
            aria-label={t('list.moreActions')}
          >
            <MoreVertical className={actionIconClass} strokeWidth={2} />
          </button>
        </DropdownMenuTrigger>
      </TruncatedTextTooltip>
      <DropdownMenuContent align='end' className={rowMenuContentClass}>
        <DropdownMenuItem
          className={rowMenuItemClass}
          onClick={() => navigate(`/invoices/${inv.id}`)}
        >
          <Eye className='size-4 shrink-0' strokeWidth={2} />
          <span className='min-w-0 flex-1'>{t('list.viewInvoice')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className={rowMenuItemClass}
          disabled={exportingPdfId === inv.id}
          onClick={() => exportPdf(inv)}
        >
          <Download className='size-4 shrink-0' strokeWidth={2} />
          <span className='min-w-0 flex-1'>{t('list.download')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const renderRowActions = (inv: Invoice) => {
    const status = inv.status as string
    const isProForma = status === 'pro_forma' || status === 'pro_forma_sent'
    const isValidated = status === 'validated'
    const needsPaymentActions =
      status === 'pending' ||
      status === 'partially_paid' ||
      status === 'overdue' ||
      status.startsWith('overdue_')
    const directSendDownloadView =
      status === 'paid' ||
      status === 'cancelled' ||
      status === 'cancelled_via_credit_note'

    if (isProForma) return [viewAction(inv), finalizeAction(inv)]
    if (isValidated)
      return [viewAction(inv), sendAction(inv), downloadAction(inv)]
    if (needsPaymentActions)
      return [tickAction(inv), sendAction(inv), moreMenuAction(inv)]
    if (directSendDownloadView)
      return [sendAction(inv), downloadAction(inv), viewAction(inv)]
    return [viewAction(inv), downloadAction(inv)]
  }

  return (
    <div className='flex min-w-0 flex-col gap-6'>
      {/* Blocking overlay while a bulk action is in flight — prevents any
          further interaction until the corresponding API resolves. */}
      {busy && (
        <div
          className='fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-sm'
          role='alertdialog'
          aria-busy='true'
          aria-live='assertive'
        >
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
          <p className='text-sm font-medium text-foreground'>
            {t(`list.bulkProgress.${bulkAction}`)}
          </p>
        </div>
      )}

      {/* Summary cards (Figma 2989:34295): icon avatar + amount + count. */}
      <div className='grid shrink-0 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        {[
          {
            key: 'total',
            labelKey: 'list.cards.totalIncTax',
            value: summary ? formatCurrency(summary.total, 'EUR', 2) : null,
            count: totalInvoiceCount,
            theme: CARD_THEMES.total,
          },
          {
            key: 'paid',
            labelKey: 'list.cards.paidInvoices',
            value: summary
              ? formatCurrency(summary.paid.total, 'EUR', 2)
              : null,
            count: summary?.paid.count ?? null,
            theme: CARD_THEMES.paid,
          },
          {
            key: 'pending',
            labelKey: 'list.cards.pendingInvoices',
            value: summary
              ? formatCurrency(summary.pending.total, 'EUR', 2)
              : null,
            count: summary?.pending.count ?? null,
            theme: CARD_THEMES.pending,
          },
          {
            key: 'overdue',
            labelKey: 'list.cards.overdueInvoices',
            value: summary
              ? formatCurrency(summary.overdue.total, 'EUR', 2)
              : null,
            count: summary?.overdue.count ?? null,
            theme: CARD_THEMES.overdue,
          },
        ].map(c => {
          const isTotalCard = c.key === 'total'
          const isOverdueCard = c.key === 'overdue'
          const statusFilter = CARD_STATUS_FILTERS[c.key as InvoiceCardKey]
          const active =
            !isTotalCard && sameValues(selectedStatuses, statusFilter)

          return (
            <button
              key={c.key}
              type='button'
              aria-pressed={active}
              onClick={() =>
                setFilters({
                  status: toCsv(statusFilter),
                })
              }
              className={cn(
                'min-w-0 rounded-2xl border p-4 text-left shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]',
                summaryCardInteractionClass,
                isTotalCard && 'overflow-hidden'
              )}
              style={{
                borderColor: active ? summaryCardActiveColor : c.theme.border,
                backgroundColor: c.theme.bg,
              }}
            >
              <div className='flex w-full items-center gap-4'>
                <div
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]',
                    isTotalCard
                      ? 'bg-[#f4f5f8] p-2'
                      : isOverdueCard
                        ? 'bg-[#FECACA]'
                        : 'bg-white'
                  )}
                >
                  <img
                    src={c.theme.iconSrc}
                    alt=''
                    className='size-4'
                    aria-hidden
                  />
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <p className='w-full text-xs font-heading font-medium leading-[1.2] text-[#5a5a5a]'>
                    {t(c.labelKey)}
                  </p>
                  <div className='flex w-full flex-col gap-1'>
                    <p
                      className='w-full text-2xl font-heading font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'
                      style={
                        c.theme.valueColor
                          ? { color: c.theme.valueColor }
                          : undefined
                      }
                    >
                      {c.value ?? '—'}
                    </p>
                    <p className='w-full text-xs font-medium leading-4 text-[#5a5a5a]'>
                      {c.count !== null
                        ? t('list.cards.invoiceCount', {
                            count: c.count,
                            formattedCount: formatInteger(c.count),
                          })
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Toolbar (Figma 2993:34149): search + filters left, export right. */}
      <div className='flex shrink-0 flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <div className='flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
          <div className='relative w-full shrink-0 sm:w-[280px]'>
            <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#272c36]' />
            <Input
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder={t('list.searchPlaceholder')}
              maxLength={100}
              className={cn(
                'h-10 w-full rounded-xl border-[#e7e9ef] bg-white pl-10 pr-4 text-sm font-normal leading-none text-[#171a20] placeholder:text-[#3d434e]',
                FORM_CONTROL_FOCUS_CLASS,
                FORM_FILTER_HOVER_CLASS
              )}
            />
          </div>

          <DateRangeFilter
            appearance='field'
            label={t('list.dateFilter')}
            from={filters.date_from}
            to={filters.date_to}
            triggerClassName={cn(
              toolbarButtonClass,
              FORM_CONTROL_FOCUS_CLASS,
              FORM_FILTER_HOVER_CLASS
            )}
            onChange={({ date_from, date_to }) =>
              setFilters({ date_from, date_to })
            }
          />

          <MultiSelectFilter
            options={STATUS_OPTIONS.map(o => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
            selected={selectedStatuses}
            onChange={values => setFilters({ status: toCsv(values) })}
            placeholder={t('list.statusPlaceholder')}
            selectedLabel={count => t('list.statusFilterSelected', { count })}
            clearLabel={t('list.allStatuses')}
            emptyLabel={t('list.statusFilterEmpty')}
            triggerClass={cn(filterTriggerClass, FORM_CONTROL_FOCUS_CLASS)}
          />

          {/* Multi-client filter (replaces the unpaid-by-client section). */}
          <ClientMultiSelectFilter
            selected={clientIds}
            triggerClassName={cn(toolbarButtonClass, FORM_CONTROL_FOCUS_CLASS)}
            onChange={ids =>
              setFilters({ client_id: ids.length ? ids.join(',') : undefined })
            }
          />
        </div>

        <Permit module='invoices' action='view'>
          <Button
            variant='outline'
            className={toolbarButtonClass}
            onClick={() => setExportOpen(true)}
          >
            <Download className='mr-2 size-4' /> {t('list.export')}
          </Button>
        </Permit>
      </div>

      {/* Bulk selection bar (feedback #37). Mutating bulk actions need invoices.edit. */}
      {canEditInvoices && selected.size > 0 && (
        <div className='flex shrink-0 flex-col gap-3 overflow-hidden rounded-xl border border-[#ff8656] bg-[#fff1ea] px-4 py-3 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-wrap items-center gap-1.5 whitespace-nowrap'>
            <span className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('list.selectedCount', { count: selected.size })}
            </span>
            <span className='text-xs font-medium leading-4 text-[#ff5a1f]'>
              {t('list.selectedUnpaidSummary', {
                count: selectedUnpaidInvoices.length,
                total: formatCurrency(selectedUnpaidTotal),
              })}
            </span>
          </div>
          <div className='flex min-w-0 flex-wrap items-center justify-end gap-2'>
            <Button
              size='sm'
              className={bulkPrimaryButtonClass}
              disabled={busy || !canSendUnpaidReminders}
              onClick={() => {
                setBulkOpen(true)
              }}
            >
              <Mail className='size-4' strokeWidth={1.75} />
              {t('list.unpaidReminders')}
            </Button>
            <Button
              size='sm'
              variant='outline'
              className={bulkSecondaryButtonClass}
              disabled={busy}
              onClick={() => setMarkPaidOpen(true)}
            >
              <Check className='size-4' strokeWidth={1.75} />
              {t('list.bulkMarkPaid')}
            </Button>
            <Button
              size='sm'
              variant='outline'
              className={bulkSecondaryButtonClass}
              disabled={busy}
              onClick={bulkChase}
            >
              <Send className='size-4' strokeWidth={1.75} />
              {t('list.sendSelected')}
            </Button>
            <Button
              size='sm'
              variant='outline'
              className={bulkSecondaryButtonClass}
              disabled={busy}
              onClick={bulkExport}
            >
              <Download className='size-4' strokeWidth={1.75} />
              {t('list.download')}
            </Button>
            <Button
              size='sm'
              variant='outline'
              className={cn(bulkSecondaryButtonClass, 'rounded-xl')}
              disabled={busy}
              onClick={clearSelection}
            >
              <X className='size-4' strokeWidth={1.75} />
              {t('list.cancelSelection')}
            </Button>
          </div>
        </div>
      )}

      <Card className='min-w-0 overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'>
        <div className='w-full overflow-x-auto'>
          <table
            className='w-full table-fixed caption-bottom text-sm'
            style={{ minWidth: TABLE_MIN_WIDTH }}
          >
            <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
              <TableRow className='border-0'>
                <TableHead className={cn(thClass, COL.checkbox)}>
                  {canEditInvoices && (
                    <Checkbox
                      checked={
                        currentPageSelectedCount > 0 &&
                        currentPageSelectedCount < invoices.length
                          ? 'indeterminate'
                          : invoices.length > 0 &&
                            currentPageSelectedCount === invoices.length
                      }
                      className={tableCheckboxClass}
                      onCheckedChange={toggleAll}
                    />
                  )}
                </TableHead>
                <TableHead className={cn(thClass, COL.clientName)}>
                  {t('list.columns.clientName')}
                </TableHead>
                <TableHead className={cn(thClass, COL.title)}>
                  {t('list.columns.title')}
                </TableHead>
                <TableHead className={cn(thClass, COL.amount)}>
                  {t('list.columns.amountIncTax')}
                </TableHead>
                <TableHead className={cn(thClass, COL.status)}>
                  {t('list.columns.status')}
                </TableHead>
                <TableHead className={cn(thClass, COL.dueDate)}>
                  {t('list.columns.dueDate')}
                </TableHead>
                <TableHead className={cn(thClass, COL.number)}>
                  {t('list.columns.number')}
                </TableHead>
                <TableHead className={cn(thClass, COL.linkedJob)}>
                  {t('list.columns.linkedJob')}
                </TableHead>
                <TableHead className={cn(thClass, COL.actions)}>
                  <div className={invoiceActionsHeaderClass}>
                    {t('list.columns.actions')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('list.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && invoices.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('list.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                invoices.map(inv => {
                  const isSelected = selected.has(inv.id)
                  return (
                    <TableRow
                      key={inv.id}
                      className={cn(
                        'group cursor-pointer border-0',
                        isSelected && '!bg-[#fff1ea]'
                      )}
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                    >
                      <TableCell
                        className={cn(tdClass, COL.checkbox, 'relative')}
                        onClick={e => e.stopPropagation()}
                      >
                        <span
                          className='absolute left-0 top-0 hidden h-16 w-1 bg-[#ff5a1f] group-hover:block'
                          aria-hidden
                        />
                        {canEditInvoices && (
                          <Checkbox
                            checked={isSelected}
                            className={tableCheckboxClass}
                            onCheckedChange={() => toggle(inv)}
                          />
                        )}
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.clientName)}>
                        <div className='min-w-0 text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'>
                          <ClientNameWithProspect
                            name={inv.client?.name ?? inv.client_name ?? '—'}
                            category={inv.client?.category}
                          />
                        </div>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.title)}>
                        <TruncatedTextTooltip
                          className='line-clamp-2 max-w-[160px] text-sm font-normal leading-[1.4] text-[#171a20]'
                          content={inv.title?.trim() || undefined}
                        >
                          {inv.title?.trim() || '—'}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.amount)}>
                        <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                          {formatCurrency(inv.total, 'EUR', 2, 'suffix')}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.status)}>
                        <InvoiceStatusBadge
                          status={inv.status}
                          dueDate={inv.due_date}
                        />
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.dueDate)}>
                        <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                          {formatDate(inv.due_date)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.number)}>
                        <TruncatedTextTooltip
                          className='line-clamp-2 max-w-[120px] text-sm font-medium leading-[1.4] text-[#171a20]'
                          content={inv.number ?? undefined}
                        >
                          {inv.number ?? '—'}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.linkedJob)}>
                        {inv.job_id && inv.job_reference ? (
                          <button
                            type='button'
                            className='block min-w-0 max-w-full text-left text-sm font-medium leading-[1.4] text-[#171a20] hover:text-[#ff5a1f] hover:underline'
                            onClick={event => {
                              event.stopPropagation()
                              openJobDrawer(inv.job_id!)
                            }}
                          >
                            <TruncatedTextTooltip
                              className='line-clamp-2'
                              content={inv.job_reference}
                            >
                              {inv.job_reference}
                            </TruncatedTextTooltip>
                          </button>
                        ) : (
                          <span className='text-sm text-[#6c7285]'>—</span>
                        )}
                      </TableCell>
                      <TableCell
                        className={cn(tdClass, COL.actions)}
                        onClick={event => event.stopPropagation()}
                      >
                        <div className={invoiceActionsGroupClass}>
                          {renderRowActions(inv).map((action, index) => (
                            <span key={index} className='contents'>
                              {action}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='shrink-0 border-t border-[#e7e9ef] bg-white'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showShowingRange={false}
        />
      </Card>

      <BulkSendInvoicesDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        mode={INVOICE_MODE.SELECTED}
        invoices={selectedUnpaidInvoices}
      />

      {/* Feedback #35: list export honouring the active search/status/date
          filters; exports as .xlsx / .csv named Invoice_list_[YYYY-MM-DD]. */}
      <ListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entityLabel='Invoice'
        rowCount={totalRows}
        onExport={async format => {
          const res = await invoicesApi.export({
            ...apiQuery,
            format,
          })
          return {
            url: res.data!.url,
            filename: res.data!.filename,
          }
        }}
      />

      <LoadingOverlay
        open={bulkAction === 'export' || exportingPdfId !== null}
      />

      {/* Row-level send-to-client, opened inline from the list. */}
      <InvoiceSendDialog
        open={!!sendTarget}
        onOpenChange={o => !o && setSendTarget(null)}
        title={t('detail.sendTitle')}
        defaultTo={
          primaryEmail(sendTarget?.client) ?? sendTarget?.client?.email
        }
        defaultSubject={t('detail.sendSubject', {
          number: sendTarget?.number ?? '',
        }).trim()}
        clientName={
          sendTarget?.client?.name ??
          sendTarget?.client_name ??
          t('detail.invoiceSendDialog.unknownClient')
        }
        reference={sendTarget?.number ?? sendTarget?.id.slice(0, 8) ?? ''}
        totalLabel={formatCurrency(sendTarget?.total ?? 0, 'EUR', 2)}
        successMessage={t('detail.sendSuccess')}
        errorMessage={t('detail.sendError')}
        onSend={async body => {
          if (!sendTarget) return
          await invoicesApi.send(sendTarget.id, {
            recipient_email: body.recipient_email,
            message: body.message || undefined,
            include_bank_details: body.include_bank_details,
          })
          // Reflect the validated -> pending status change without a manual refresh.
          queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
          queryClient.invalidateQueries({ queryKey: ['invoices', 'summary'] })
          invalidatePortalMetricQueries(queryClient)
        }}
      />

      <JobDetailDrawer
        jobId={selectedJobId}
        initialTab={searchParams.get('job_tab') ?? undefined}
        onClose={closeJobDrawer}
      />

      {/* Mark-as-paid confirm (feedback #37). */}
      <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
        <DialogContent className='max-w-[600px] gap-0 border-border p-8 sm:rounded-lg'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-3 pr-8'>
              <DialogTitle className='text-2xl font-semibold leading-[1.2] tracking-[-0.48px]'>
                {t('list.bulkMarkPaidConfirmTitle')}
              </DialogTitle>
              <p className='text-base leading-[1.4] text-foreground'>
                {t('list.bulkMarkPaidConfirmText', { count: selected.size })}
              </p>
            </div>

            <div className='flex items-center justify-end gap-4'>
              <Button
                variant='outline'
                className='rounded-lg border-[#767676] px-3 py-3 text-base font-normal'
                onClick={() => setMarkPaidOpen(false)}
                disabled={busy}
              >
                {t('deleteInvoice.cancel')}
              </Button>
              <Button
                className='rounded-lg px-3 py-3 text-base font-normal text-white'
                disabled={busy}
                onClick={bulkMarkPaid}
              >
                {t('list.bulkMarkPaid')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
